import React, { useState, useEffect } from 'react';
import { Save, TrendingUp, Edit } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MetricsSettings = () => {
  const { toast } = useToast();
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    activeClients: 0,
    totalRevenue: 0,
    successRate: 0,
    clientsToday: 0,
    avgRating: 4.8,
    useFakeMetrics: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/metrics`);
      if (response.data) {
        setMetrics(response.data);
      }
    } catch (error) {
      console.error('Error fetching metrics settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings/metrics`, metrics);
      toast({
        title: '✅ Success',
        description: 'Metrics settings updated successfully',
      });
    } catch (error) {
      toast({
        title: '❌ Error',
        description: 'Failed to update metrics settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Live Metrics Settings</h2>
          <p className="text-slate-600">Control what metrics are displayed on the landing page</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Use Fake Metrics Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Display Mode</h3>
            <p className="text-sm text-slate-600">Choose between real or custom metrics</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={metrics.useFakeMetrics}
              onChange={(e) => setMetrics({...metrics, useFakeMetrics: e.target.checked})}
            />
            <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm font-medium text-slate-700">
              {metrics.useFakeMetrics ? 'Custom Metrics' : 'Real Metrics'}
            </span>
          </label>
        </div>

        {metrics.useFakeMetrics && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Custom metrics will be displayed on the landing page to increase conversion. These are not real-time values.
            </p>
          </div>
        )}
      </div>

      {/* Metrics Input */}
      {metrics.useFakeMetrics && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Custom Metric Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Total Clients
              </label>
              <input
                type="number"
                value={metrics.totalClients}
                onChange={(e) => setMetrics({...metrics, totalClients: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 5000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Active Clients
              </label>
              <input
                type="number"
                value={metrics.activeClients}
                onChange={(e) => setMetrics({...metrics, activeClients: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 3500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Total Revenue (₹)
              </label>
              <input
                type="number"
                value={metrics.totalRevenue}
                onChange={(e) => setMetrics({...metrics, totalRevenue: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 50000000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Success Rate (%)
              </label>
              <input
                type="number"
                value={metrics.successRate}
                onChange={(e) => setMetrics({...metrics, successRate: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 92.5"
                step="0.1"
                max="100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Clients Helped Today
              </label>
              <input
                type="number"
                value={metrics.clientsToday}
                onChange={(e) => setMetrics({...metrics, clientsToday: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 15"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Average Rating
              </label>
              <input
                type="number"
                value={metrics.avgRating}
                onChange={(e) => setMetrics({...metrics, avgRating: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 4.8"
                step="0.1"
                max="5"
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Preview</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{metrics.totalClients}+</div>
            <div className="text-xs text-slate-600 mt-1">Total Clients</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{metrics.activeClients}+</div>
            <div className="text-xs text-slate-600 mt-1">Active Clients</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">₹{(metrics.totalRevenue / 1000000).toFixed(1)}M</div>
            <div className="text-xs text-slate-600 mt-1">Revenue</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{metrics.successRate}%</div>
            <div className="text-xs text-slate-600 mt-1">Success Rate</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{metrics.clientsToday}</div>
            <div className="text-xs text-slate-600 mt-1">Helped Today</div>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600">{metrics.avgRating}</div>
            <div className="text-xs text-slate-600 mt-1">Rating</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsSettings;
